import { formatNumber } from '@sortlist-frontend/utils';
import { useState } from 'react';

import type { GuideField, GuideLabels } from '../types';
import { BarTrack } from './BarTrack';
import { NumberField } from './NumberField';

interface ScopeSimulatorProps {
  fields: readonly GuideField[];
  locale: string;
  labels: Pick<GuideLabels, 'simulatorScenarios' | 'simulatorRows' | 'simulatorEmpty' | 'simulatorCheaper'>;
}

const numberOf = (value: string | undefined) => Math.max(0, Number(value) || 0);

export const ScopeSimulator = (props: ScopeSimulatorProps) => {
  const { fields, locale, labels } = props;
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.id, field.value ?? ''])),
  );
  const days = numberOf(values['sim-j']);
  const dailyRate = numberOf(values['sim-t']);
  const fixedPrice = numberOf(values['sim-p']);
  const billedShare = Math.min(100, numberOf(values['sim-a'])) / 100;
  const rows = labels.simulatorScenarios.map((scenario) => ({
    label: scenario.label,
    timeAndMaterials: days * dailyRate * (1 + scenario.growth),
    fixedPrice: fixedPrice * (1 + scenario.growth * billedShare),
  }));
  const highest = Math.max(1, ...rows.flatMap((row) => [row.timeAndMaterials, row.fixedPrice]));
  const cheaper = (row: (typeof rows)[number]) =>
    row.fixedPrice === row.timeAndMaterials
      ? labels.simulatorCheaper.tie
      : row.fixedPrice < row.timeAndMaterials
        ? labels.simulatorCheaper.fixedPrice
        : labels.simulatorCheaper.timeAndMaterials;
  const message =
    days * dailyRate === 0 || fixedPrice === 0
      ? labels.simulatorEmpty
      : rows
          .map((row) => `${row.label.toLowerCase()} : ${cheaper(row)} ${labels.simulatorCheaper.suffix}`)
          .join(' · ') + '.';
  const euros = (value: number) => `${formatNumber({ value: Math.round(value), locale })} €`;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1fr_1.2fr]">
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((field) => (
          <NumberField
            key={field.id}
            field={field}
            value={values[field.id] ?? ''}
            onChange={(value) => setValues({ ...values, [field.id]: value })}
          />
        ))}
      </div>
      <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5 md:p-6" aria-live="polite">
        {rows.map((row) => (
          <div key={row.label} className="mb-4">
            <p className="mb-1 text-sm font-semibold text-secondary-950">{row.label}</p>
            <p className="mb-0.5 flex justify-between text-xs text-secondary-700">
              <span>{labels.simulatorRows.timeAndMaterials}</span>
              <strong>{euros(row.timeAndMaterials)}</strong>
            </p>
            <BarTrack label={labels.simulatorRows.timeAndMaterials} ratio={row.timeAndMaterials / highest} />
            <p className="mb-0.5 mt-2 flex justify-between text-xs text-secondary-700">
              <span>{labels.simulatorRows.fixedPrice}</span>
              <strong>{euros(row.fixedPrice)}</strong>
            </p>
            <BarTrack label={labels.simulatorRows.fixedPrice} ratio={row.fixedPrice / highest} isMuted />
          </div>
        ))}
        <p className="text-sm font-semibold leading-relaxed text-secondary-950">{message}</p>
      </div>
    </div>
  );
};
